import { useCallback, useEffect, useRef } from 'react';
import { WaveSurferInstance } from './useWaveSurferPlayer';

interface UsePlayerEventsProps {
  setIsPlayerReady: (isReady: boolean) => void;
  isOpen: boolean;
}

export const usePlayerEvents = (
  player: WaveSurferInstance | null | undefined,
  props: UsePlayerEventsProps,
  setCurrentTime: (duration: number) => void
) => {
  const audio = useRef<HTMLAudioElement | null>(null);
  const isDragging = useRef<boolean>(false);
  const isPlaying = useRef<boolean>(false);
  const { setIsPlayerReady, isOpen } = props;
  const duration = isOpen ? player?.getDuration() || 0 : 0;
  const readyHandler = useCallback(() => {
    setIsPlayerReady(true);
  }, [setIsPlayerReady]);

  const loadHandler = useCallback(() => {
    audio.current?.pause();
    audio.current = null;
  }, []);

  const handleMouseDown = useCallback(
    (e) => {
      if (!player) return;

      isDragging.current = true;
      isPlaying.current = player.isPlaying();

      if (isPlaying.current) {
        player.pause();
        const currentTime = player.getCurrentTime();
        if (!audio.current) {
          audio.current = new Audio(player.getMediaElement().src);
        }
        audio.current.currentTime = currentTime;
        audio.current.play();
      }

      const progress = e.offsetX / player.getWidth();
      player.seekTo(progress);
    },
    [player]
  );

  const handleMouseMove = useCallback(
    (e) => {
      if (!isDragging.current || !player) return;

      const progress = e.offsetX / player.getWidth();
      player.seekTo(progress);
      setCurrentTime(player.getCurrentTime());
    },
    [player, setCurrentTime]
  );

  const handleMouseUp = useCallback(
    (e) => {
      if (!player) return;

      isDragging.current = false;

      audio.current?.pause();
      audio.current = null;

      if (isPlaying.current) {
        isPlaying.current = false;
        player.play();
      }

      const progress = e.offsetX / player.getWidth();
      player.seekTo(progress);
    },
    [player]
  );

  const handleMouseLeave = useCallback(() => {
    audio.current?.pause();
    audio.current = null;
    isDragging.current = false;
    if (isPlaying.current) {
      isPlaying.current = false;
      player?.play();
    }
  }, [player]);

  useEffect(() => {
    if (player) {
      player.un('ready', readyHandler);
      player.on('ready', readyHandler);
      player.un('finish', loadHandler);
      player.on('finish', loadHandler);

      player.getWrapper().removeEventListener('mousedown', handleMouseDown);
      player.getWrapper().addEventListener('mousedown', handleMouseDown);

      player.getWrapper().removeEventListener('mousemove', handleMouseMove);
      player.getWrapper().addEventListener('mousemove', handleMouseMove);

      player.getWrapper().removeEventListener('mouseup', handleMouseUp);
      player.getWrapper().addEventListener('mouseup', handleMouseUp);

      player.getWrapper().removeEventListener('mouseleave', handleMouseLeave);
      player.getWrapper().addEventListener('mouseleave', handleMouseLeave);
    }
  }, [
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
    handleMouseLeave,
    player,
    readyHandler,
    loadHandler,
  ]);

  return { duration };
};
